import { useState } from 'react';
import { useAuthStore } from 'src/store/auth';
import { RoleType } from 'src/constants';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>(
    {}
  );
  const { login, loading, error } = useAuthStore();

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await login(email, password);
      const { user } = useAuthStore.getState();
      if (user && user.role === RoleType.AI) {
        throw new Error('AI role cannot sign in to dashboard');
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, form: 'Login failed. Please verify your email and password.' }));
    }
  };

  return (
    <div className="min-h-screen relative signin-container">
      <div className="absolute left-0 right-0 flex justify-center signin-logo">
        <img className="max-sm:w-28" src="/icons/onehealth-logo.svg" alt="logo" />
      </div>

      <div className="min-h-screen flex items-center max-md:items-baseline justify-center">
        <div className="w-full max-w-md max-sm:max-w-sm max-[440px]:max-w-xs max-md:mt-40">
          <div className="text-center mb-10">
            <h2 className="text-5xl max-md:text-[32px] text-white font-medium">Sign in</h2>
          </div>

          <form id="signin-form" onSubmit={handleSubmit} className="space-y-2">
            <div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                className={`w-full px-4 py-3 rounded-md text-white placeholder-gray-300 outline-none signin-input ${
                  errors.email ? 'border-2 border-red-500' : ''
                }`}
                placeholder="Email"
                autoComplete="off"
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div className="mb-6">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                className={`w-full px-4 py-3 rounded-md text-white placeholder-gray-300 outline-none signin-input ${
                  errors.password ? 'border-2 border-red-500' : ''
                }`}
                placeholder="Password"
                autoComplete="off"
              />
              {errors.password && (
                <p className="text-red-400 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {((error as string | null) || errors.form) && (
              <p className="text-red-400 text-sm mt-1 text-center">{errors.form || error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full !mt-6 py-2 px-4 rounded-full text-lg signin-button hidden sm:block ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 sm:hidden">
        <div className="w-full max-w-md mx-auto">
          <button
            type="submit"
            form="signin-form"
            disabled={loading}
            className={`w-full py-2 px-4 max-sm:text-sm rounded-full text-lg signin-button ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
